import React from "react";
import { Button, Divider, Grid, Typography } from "@mui/material";
import { openEmbeddedPropertyContractSign } from "utils/property-contract-embedded-sign";

const getErrorMessage = (e: unknown): string => {
  if (e && typeof e === "object" && "message" in e) {
    return String((e as { message: unknown }).message);
  }
  return "Failed to start signing";
};

type CreateEmbeddedResult = {
  signUrl: string;
  signatureRequestId?: string;
  id?: string;
};

type SupervisorSignProps = {
  open: (url: string) => Promise<{ signatureId: string }>;
  createEmbeddedRequest: () => Promise<CreateEmbeddedResult>;
  cancelIfNeeded: () => Promise<void>;
  contractId?: string | null;
  orgId?: string;
  propertyName?: string;
  onGoToSuccess?: (data: { variant: "supervisor"; contractId?: string | null; orgId?: string }) => void;
};

const SupervisorSignForm: React.FC<{
  onSign: () => void;
  busy: boolean;
  error: string | null;
  propertyName?: string;
}> = ({ onSign, busy, error, propertyName }) => (
  <>
    <Typography variant="h6">
      Sign {propertyName ?? ""} Contract
    </Typography>
    <Divider style={{ width: "100%", margin: "8px 0" }} />
    {error && (
      <Typography variant="body2" color="error" gutterBottom>
        {error}
      </Typography>
    )}
    <Grid size={{ xs: 12 }} style={{ marginTop: 16 }}>
      <Button variant="contained" color="primary" onClick={onSign} disabled={busy}>
        Sign Now
      </Button>
    </Grid>
  </>
);

const useSupervisorSignActions = (props: SupervisorSignProps) => {
  const { open, createEmbeddedRequest, cancelIfNeeded, onGoToSuccess, contractId, orgId } = props;
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSupervisorSign = React.useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const { id: createdContractId } = await openEmbeddedPropertyContractSign({
        open,
        cancelIfNeeded,
        prepareAndGetSignUrl: createEmbeddedRequest,
      });
      onGoToSuccess?.({
        variant: "supervisor",
        contractId: createdContractId ?? contractId ?? null,
        orgId,
      });
    } catch (e: unknown) {
      const errMsg = getErrorMessage(e);
      setError(errMsg);
    } finally {
      setBusy(false);
    }
  }, [cancelIfNeeded, createEmbeddedRequest, open, onGoToSuccess, contractId, orgId]);

  return { busy, error, handleSupervisorSign };
};

const SupervisorSign: React.FC<SupervisorSignProps> = (props) => {
  const { busy, error, handleSupervisorSign } = useSupervisorSignActions(props);
  return (
    <SupervisorSignForm
      onSign={handleSupervisorSign}
      busy={busy}
      error={error}
      propertyName={props.propertyName}
    />
  );
};

export default SupervisorSign;
